import { cardBlocksToHtml } from "@shared/lib/card-stack/unified-migration"
import type { CourseDateAnnotationSingle } from "@shared/models/course"
import { Editor, isActive, mergeAttributes, Node } from "@tiptap/core"
import type { Node as PMNode } from "@tiptap/pm/model"
import { Plugin, type EditorState } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { JoinListDOMSerializer } from "tiptap-extension-flat-list"
import {
  cancelBackspaceAtContentStart,
  cancelBackspaceAtNodeStart,
} from "../../util/cancel-backspace-at-node-start"
import { renderStringAsData } from "../../util/render-data"
import { CARD_VALUE_SCHEMA } from "../cards/card-extensions"
import {
  DateAnnotationNoteComponent,
  DateAnnotationNoteTitleComponent,
} from "./date-annotation-note-component"

export interface ExtensionDateAnnotationNoteAttrs {
  id: string
  color: string
  dateString: string
}

/**
 * What a note says. The annotation's own two fields, so the editor and the day's notes hold one
 * shape between them — including its rule that a field with nothing in it is null.
 */
export type NoteWords = Pick<CourseDateAnnotationSingle["attributes"], "title" | "description">

/**
 * A note's field, or null when there is nothing in it. Notes saved before this editor hold an
 * empty description as `"<p></p>"` and an empty title as `""`, which are both the model's null.
 */
export function noteFieldOrNull(value: string | null): string | null {
  if (value === null) return null
  return value.trim() === "" || value === "<p></p>" ? null : value
}

/** An empty line, which is what the schema's required block is until the user types. */
const isBlankLine = (node: PMNode) => node.type.name === "paragraph" && node.content.size === 0

/** What the note says under its title. The blank line Enter leaves behind is not words. */
function descriptionBlocks(note: PMNode): PMNode[] {
  const blocks: PMNode[] = []
  note.content.cut(note.firstChild?.nodeSize ?? 0).forEach((block) => blocks.push(block))
  while (blocks.length > 0 && isBlankLine(blocks[blocks.length - 1])) blocks.pop()
  return blocks
}

/** The note this doc holds, or null when the doc holds no note. */
export function readNoteWords(state: EditorState): NoteWords | null {
  const note = state.doc.firstChild
  if (!note || note.type.name !== "dateAnnotationNote") return null

  const blocks = descriptionBlocks(note)
  return {
    title: noteFieldOrNull(note.firstChild?.textContent ?? ""),
    description:
      blocks.length === 0
        ? null
        : cardBlocksToHtml(blocks, JoinListDOMSerializer.fromSchema(state.schema, "getHTML")),
  }
}

/**
 * Whether the note's description belongs on screen. A note is its title; the words under it are
 * for the days that need them, so an empty description waits until the caret is in it — Enter
 * from the title, or a press below it.
 */
export function noteDescriptionIsShowing(state: EditorState): boolean {
  const note = state.doc.firstChild
  if (!note || note.type.name !== "dateAnnotationNote") return true
  if (descriptionBlocks(note).length > 0) return true
  return !isActive(state, "dateAnnotationNoteTitle")
}

/**
 * Enter in a note's title drops into its description. At the end of the title there is nothing
 * to carry, and the note's content spec (`block+` after the title) already keeps a line waiting
 * — so move the caret there rather than splitting another line into being. Anywhere else is an
 * ordinary split, which carries the rest of the title down with the caret.
 */
function enterFromNoteTitle(editor: Editor): boolean {
  if (!editor.isActive("dateAnnotationNoteTitle")) return false

  // Tiptap's `isAtEndOfNode` answers this a position early, so ask ProseMirror directly.
  const { $from, empty } = editor.state.selection
  if (!empty || $from.parentOffset !== $from.parent.content.size) {
    return editor.commands.splitBlock()
  }

  const title = editor.state.doc.firstChild?.firstChild
  if (!title) return false
  return editor.commands.setTextSelection(1 + title.nodeSize + 1)
}

export const ExtensionDateAnnotationNoteTitle = Node.create({
  name: "dateAnnotationNoteTitle",
  content: "text*",
  marks: "",

  renderHTML({ HTMLAttributes }) {
    return [
      "h2",
      mergeAttributes(HTMLAttributes, {
        "data-content-type": "dateAnnotationNoteTitle",
      }),
      0,
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'h2[data-content-type="dateAnnotationNoteTitle"]',
      },
    ]
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => enterFromNoteTitle(this.editor),
      "Shift-Enter": () => enterFromNoteTitle(this.editor),
      Backspace: () => cancelBackspaceAtNodeStart(this.editor, this.name),
      "Shift-Backspace": () => cancelBackspaceAtNodeStart(this.editor, this.name),
      "Mod-Backspace": () => cancelBackspaceAtNodeStart(this.editor, this.name),
      "Alt-Backspace": () => cancelBackspaceAtNodeStart(this.editor, this.name),
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(DateAnnotationNoteTitleComponent)
  },
})

export const ExtensionDateAnnotationNote = Node.create({
  name: "dateAnnotationNote",
  group: "blockContainer addTarget",
  content: "dateAnnotationNoteTitle " + CARD_VALUE_SCHEMA,
  draggable: false,

  addAttributes() {
    return {
      id: {
        ...renderStringAsData("id"),
        default: "",
      },
      color: {
        ...renderStringAsData("color"),
        default: "yellow",
      },
      dateString: {
        ...renderStringAsData("dateString"),
        default: "",
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-content-type": "dateAnnotationNote" }),
      0,
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-content-type="dateAnnotationNote"]',
      },
    ]
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () => cancelBackspaceAtContentStart(this.editor, this.name),
      "Shift-Backspace": () => cancelBackspaceAtContentStart(this.editor, this.name),
      "Mod-Backspace": () => cancelBackspaceAtContentStart(this.editor, this.name),
      "Alt-Backspace": () => cancelBackspaceAtContentStart(this.editor, this.name),
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(DateAnnotationNoteComponent)
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        filterTransaction(tr) {
          if (tr.before.firstChild?.type.name === "dateAnnotationNote") {
            if (tr.doc.childCount === 0 || tr.doc.firstChild?.type.name !== "dateAnnotationNote") {
              return false
            }
          }
          return true
        },
      }),
    ]
  },
})
